import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import axios from "axios";
import Swal from "sweetalert2";

const API =
  process.env.REACT_APP_API_BASE_URL;

// =========================================
// DATE HELPERS
// =========================================

const getToday = () => {
  const d = new Date();

  const month = String(
    d.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    d.getDate()
  ).padStart(2, "0");

  return `${d.getFullYear()}-${month}-${day}`;
};

const formatDate = (date) => {
  if (!date) return "-";

  const parts = String(date).split("-");

  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  return date;
};

// =========================================
// CALENDAR DAYS
// =========================================

const getDaysBetween = (
  startDate,
  endDate
) => {
  if (!startDate || !endDate) {
    return 0;
  }

  const start = new Date(
    `${startDate}T00:00:00`
  );

  const end = new Date(
    `${endDate}T00:00:00`
  );

  const difference =
    end.getTime() -
    start.getTime();

  if (difference < 0) {
    return 0;
  }

  return (
    Math.floor(
      difference /
        (1000 * 60 * 60 * 24)
    ) + 1
  );
};

// =========================================
// NORMALIZE PENDING ITEM
// =========================================

const normalizePendingItem = (
  item,
  reportSubject = ""
) => {
  if (!item) {
    return null;
  }

  /*
    We always want:

    {
      subject: "Maths",
      type: "Homework"
    }

    Never:

    {
      subject: "Homework",
      type: "Maths"
    }
  */

  let subject =
    item.subject ||
    reportSubject ||
    "";

  let type =
    item.type ||
    "";

  subject = String(subject).trim();
  type = String(type).trim();

  const lowerSubject =
    subject.toLowerCase();

  const lowerType =
    type.toLowerCase();

  // If backend accidentally sends reversed data:
  // subject = Homework
  // type = Maths

  if (
    lowerSubject === "homework" ||
    lowerSubject === "notebook"
  ) {
    const realType =
      subject;

    const realSubject =
      type;

    subject = realSubject;
    type =
      realType.charAt(0).toUpperCase() +
      realType.slice(1).toLowerCase();
  }

  // Normalize type
  if (
    type.toLowerCase() ===
    "homework"
  ) {
    type = "Homework";
  }

  if (
    type.toLowerCase() ===
    "notebook"
  ) {
    type = "Notebook";
  }

  if (!subject) {
    return null;
  }

  if (
    type !== "Homework" &&
    type !== "Notebook"
  ) {
    return null;
  }

  return {
    subject,
    type
  };
};

// =========================================
// COMPONENT
// =========================================

const MyClass = () => {
  // =========================================
  // TEACHER
  // =========================================

  const [teacher, setTeacher] =
    useState(null);

  // =========================================
  // REPORTS
  // =========================================

  const [reports, setReports] =
    useState([]);

  // =========================================
  // CLASS STUDENTS
  // =========================================

  const [classStudents, setClassStudents] =
    useState([]);

  // =========================================
  // FILTERS
  // =========================================

  const [selectedDate, setSelectedDate] =
    useState(getToday);

  const [subjectFilter, setSubjectFilter] =
    useState("all");

  const [selectedStudentId, setSelectedStudentId] =
    useState("all");

  // =========================================
  // WHATSAPP SELECTION
  // =========================================

  const [selectedStudents, setSelectedStudents] =
    useState({});

  // =========================================
  // HISTORY MODAL
  // =========================================

  const [
    selectedHistoryStudent,
    setSelectedHistoryStudent
  ] = useState(null);
  const [historyMonth, setHistoryMonth] = useState("all");

  const historyMonths = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];

  // =========================================
  // LOADING
  // =========================================

  const [loading, setLoading] =
    useState(true);

  const [reportsLoading, setReportsLoading] =
    useState(false);

  const [studentsLoading, setStudentsLoading] =
    useState(false);

  // =========================================
  // WHATSAPP STATES
  // =========================================

  const [sendingStudentId, setSendingStudentId] =
    useState(null);

  const [bulkSending, setBulkSending] =
    useState(false);

  // =========================================
  // MESSAGE
  // =========================================

  const [message, setMessage] =
    useState("");

  const [error, setError] =
    useState("");

  // =========================================
  // SESSION
  // =========================================

  const sessionId =
    localStorage.getItem(
      "currentSessionId"
    ) ||
    localStorage.getItem(
      "sessionId"
    );

  // =========================================
  // TEACHER ID
  // =========================================

  const teacherId = useMemo(() => {
    try {
      const data = JSON.parse(
        localStorage.getItem(
          "teacher"
        ) || "null"
      );

      return (
        data?._id ||
        data?.id ||
        data?.teacherId ||
        null
      );
    } catch {
      return null;
    }
  }, []);

  // =========================================
  // LOAD TEACHER
  // =========================================

  useEffect(() => {
    const loadTeacher = async () => {
      if (
        !teacherId ||
        !sessionId
      ) {
        setLoading(false);

        setError(
          "Teacher or session missing."
        );

        return;
      }

      try {
        setLoading(true);
        setError("");

        const res =
          await axios.get(
            `${API}/api/teachers/getTeacherById/${teacherId}`,
            {
              params: {
                sessionId
              }
            }
          );

        setTeacher(res.data);
      } catch (err) {
        console.error(
          "Teacher loading error:",
          err
        );

        setError(
          err.response?.data?.message ||
            "Unable to load teacher."
        );
      } finally {
        setLoading(false);
      }
    };

    loadTeacher();
  }, [
    teacherId,
    sessionId
  ]);

  // =========================================
  // CLASS INCHARGE
  // =========================================

  const classIncharge =
    teacher?.classIncharge || "";

  const classSection = useMemo(() => {
    if (!classIncharge) {
      return {
        className: "",
        sectionName: ""
      };
    }

    const parts =
      classIncharge.split(" - ");

    if (parts.length >= 2) {
      return {
        className:
          parts[0].trim(),

        sectionName:
          parts
            .slice(1)
            .join(" - ")
            .trim()
      };
    }

    return {
      className:
        classIncharge.trim(),

      sectionName: ""
    };
  }, [classIncharge]);

  // =========================================
  // LOAD REPORTS
  // =========================================

  const loadReports = async () => {
    if (
      !teacherId ||
      !sessionId
    ) {
      return;
    }

    try {
      setReportsLoading(true);
      setError("");

      const res =
        await axios.get(
          `${API}/api/homeworkNotCompleted/received`,
          {
            params: {
              teacherId,
              sessionId
            }
          }
        );

      const data =
        Array.isArray(res.data)
          ? res.data
          : res.data?.reports || [];

      setReports(data);
    } catch (err) {
      console.error(
        "Reports loading error:",
        err
      );

      setReports([]);

      setError(
        err.response?.data?.message ||
          "Unable to load homework reports."
      );
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [
    teacherId,
    sessionId
  ]);

  // =========================================
  // LOAD CLASS STUDENTS
  // =========================================

  const loadClassStudents =
    async () => {
      if (
        !classSection.className ||
        !classSection.sectionName ||
        !sessionId
      ) {
        setClassStudents([]);
        return;
      }

      try {
        setStudentsLoading(true);

        const res =
          await axios.get(
            `${API}/api/students/getStudents`,
            {
              params: {
                sessionId,

                class:
                  classSection.className,

                section:
                  classSection.sectionName,

                status: "active"
              }
            }
          );

        const data =
          Array.isArray(res.data)
            ? res.data
            : res.data?.students || [];

        const sorted =
          [...data].sort(
            (a, b) =>
              Number(
                a.rollNo || 999999
              ) -
              Number(
                b.rollNo || 999999
              )
          );

        setClassStudents(sorted);
      } catch (err) {
        console.error(
          "Class students loading error:",
          err
        );

        setClassStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };

  useEffect(() => {
    loadClassStudents();
  }, [
    classSection.className,
    classSection.sectionName,
    sessionId
  ]);

  // =========================================
  // AVAILABLE SUBJECTS
  // =========================================

  const availableSubjects =
    useMemo(() => {
      const subjects =
        new Set();

      reports.forEach(
        (report) => {
          if (
            classSection.className &&
            String(
              report.className
            ) !==
              String(
                classSection.className
              )
          ) {
            return;
          }

          if (
            classSection.sectionName &&
            String(
              report.sectionName
            ) !==
              String(
                classSection.sectionName
              )
          ) {
            return;
          }

          if (report.subject) {
            subjects.add(
              report.subject
            );
          }
        }
      );

      return Array.from(
        subjects
      ).sort();
    }, [
      reports,
      classSection
    ]);

  // =========================================
  // DATE REPORTS
  // =========================================

  const dateReports =
    useMemo(() => {
      return reports.filter(
        (report) => {
          if (
            report.date !==
            selectedDate
          ) {
            return false;
          }

          if (
            classSection.className &&
            String(
              report.className
            ) !==
              String(
                classSection.className
              )
          ) {
            return false;
          }

          if (
            classSection.sectionName &&
            String(
              report.sectionName
            ) !==
              String(
                classSection.sectionName
              )
          ) {
            return false;
          }

          if (
            subjectFilter !==
              "all" &&
            report.subject !==
              subjectFilter
          ) {
            return false;
          }

          return true;
        }
      );
    }, [
      reports,
      selectedDate,
      subjectFilter,
      classSection
    ]);

  // =========================================
  // ALL CLASS REPORTS
  // =========================================

  const classReports =
    useMemo(() => {
      return reports.filter(
        (report) => {
          if (
            classSection.className &&
            String(
              report.className
            ) !==
              String(
                classSection.className
              )
          ) {
            return false;
          }

          if (
            classSection.sectionName &&
            String(
              report.sectionName
            ) !==
              String(
                classSection.sectionName
              )
          ) {
            return false;
          }

          return true;
        }
      );
    }, [
      reports,
      classSection
    ]);

  // =========================================
  // STUDENT DAY STATS
  // =========================================

  const getStudentDayStats =
    (studentId) => {
      const homeworkDates =
        new Set();

      const notebookDates =
        new Set();

      classReports.forEach(
        (report) => {
          (
            report.students || []
          ).forEach(
            (student) => {
              if (
                String(
                  student.studentId
                ) !==
                  String(
                    studentId
                  )
              ) {
                return;
              }

              if (
                student.notCompletedHomework
              ) {
                homeworkDates.add(
                  report.date
                );
              }

              if (
                student.notCompletedNotebook
              ) {
                notebookDates.add(
                  report.date
                );
              }
            }
          );
        }
      );

      const homeworkSorted =
        Array.from(
          homeworkDates
        ).sort();

      const notebookSorted =
        Array.from(
          notebookDates
        ).sort();

      const homeworkSince =
        homeworkSorted[0] ||
        null;

      const notebookSince =
        notebookSorted[0] ||
        null;

      return {
        homeworkDates:
          homeworkSorted,

        notebookDates:
          notebookSorted,

        homeworkDays:
          homeworkSorted.length,

        notebookDays:
          notebookSorted.length,

        homeworkSince,

        notebookSince,

        homeworkCalendarDays:
          homeworkSince
            ? getDaysBetween(
                homeworkSince,
                selectedDate
              )
            : 0,

        notebookCalendarDays:
          notebookSince
            ? getDaysBetween(
                notebookSince,
                selectedDate
              )
            : 0
      };
    };

  // =========================================
  // STUDENTS FOR SELECTED DATE
  // =========================================

  const studentsForSelectedDate =
    useMemo(() => {
      const studentsMap =
        {};

      dateReports.forEach(
        (report) => {
          (
            report.students || []
          ).forEach(
            (student) => {
              const id =
                String(
                  student.studentId
                );

              if (
                selectedStudentId !==
                  "all" &&
                id !==
                  String(
                    selectedStudentId
                  )
              ) {
                return;
              }

              if (
                !studentsMap[id]
              ) {
                studentsMap[id] = {
                  studentId:
                    student.studentId,

                  rollNo:
                    student.rollNo,

                  studentName:
                    student.studentName ||
                    "-",

                  fatherName:
                    student.fatherName ||
                    "-",

                  phoneNo:
                    student.phoneNo,

                  phoneNo2:
                    student.phoneNo2,

                  subjectsMap: {}
                };
              }

              if (
                !studentsMap[id]
                  .subjectsMap[
                  report.subject
                ]
              ) {
                studentsMap[id]
                  .subjectsMap[
                  report.subject
                ] = {
                  homework:
                    false,

                  notebook:
                    false
                };
              }

              if (
                student.notCompletedHomework
              ) {
                studentsMap[id]
                  .subjectsMap[
                  report.subject
                ].homework =
                  true;
              }

              if (
                student.notCompletedNotebook
              ) {
                studentsMap[id]
                  .subjectsMap[
                  report.subject
                ].notebook =
                  true;
              }
            }
          );
        }
      );

      return Object.values(
        studentsMap
      )
        .map((student) => {
          const pendingItems =
            [];

          Object.entries(
            student.subjectsMap
          ).forEach(
            ([
              subject,
              types
            ]) => {
              if (
                types.homework
              ) {
                pendingItems.push({
                  subject:
                    String(
                      subject
                    ).trim(),

                  type:
                    "Homework"
                });
              }

              if (
                types.notebook
              ) {
                pendingItems.push({
                  subject:
                    String(
                      subject
                    ).trim(),

                  type:
                    "Notebook"
                });
              }
            }
          );

          return {
            ...student,
            pendingItems
          };
        })
        .sort(
          (a, b) =>
            Number(
              a.rollNo ??
                999999
            ) -
            Number(
              b.rollNo ??
                999999
            )
        );
    }, [
      dateReports,
      selectedStudentId
    ]);

  // =========================================
  // STUDENT HISTORY
  // =========================================

  const getStudentHistory =
    (studentId) => {
      const historyMap =
        {};

      classReports.forEach(
        (report) => {
          const student =
            (
              report.students ||
              []
            ).find(
              (item) =>
                String(
                  item.studentId
                ) ===
                String(
                  studentId
                )
            );

          if (!student) {
            return;
          }

          if (
            !historyMap[
              report.date
            ]
          ) {
            historyMap[
              report.date
            ] = {
              date:
                report.date,

              subjects: {}
            };
          }

          if (
            !historyMap[
              report.date
            ].subjects[
              report.subject
            ]
          ) {
            historyMap[
              report.date
            ].subjects[
              report.subject
            ] = {
              homework:
                false,

              notebook:
                false
            };
          }

          if (
            student.notCompletedHomework
          ) {
            historyMap[
              report.date
            ].subjects[
              report.subject
            ].homework =
              true;
          }

          if (
            student.notCompletedNotebook
          ) {
            historyMap[
              report.date
            ].subjects[
              report.subject
            ].notebook =
              true;
          }
        }
      );

      const rows = [];

      Object.values(
        historyMap
      ).forEach(
        (history) => {
          Object.entries(
            history.subjects
          ).forEach(
            ([
              subject,
              types
            ]) => {
              rows.push({
                date:
                  history.date,

                subject,

                homework:
                  types.homework,

                notebook:
                  types.notebook
              });
            }
          );
        }
      );

      return rows.sort(
        (a, b) => {
          const dateCompare =
            String(
              b.date
            ).localeCompare(
              String(
                a.date
              )
            );

          if (
            dateCompare !== 0
          ) {
            return dateCompare;
          }

          return String(
            a.subject
          ).localeCompare(
            String(
              b.subject
            )
          );
        }
      );
    };

  const filteredHistory = useMemo(() => {
    const history = selectedHistoryStudent?.history || [];

    if (historyMonth === "all") {
      return history;
    }

    return history.filter((row) => {
      const month = String(row.date || "").split("-")[1];
      return month === historyMonth;
    });
  }, [selectedHistoryStudent, historyMonth]);

  // =========================================
  // OPEN STUDENT HISTORY
  // =========================================

  const openStudentHistory = (student) => {
  setHistoryMonth("all");

  setSelectedHistoryStudent({
    ...student,
    history: getStudentHistory(
      student.studentId
    )
  });
};

  // =========================================
  // PHONE
  // =========================================

  const getPhone = (
    student
  ) => {
    return (
      student.phoneNo ||
      student.phoneNo2 ||
      ""
    );
  };

  // =========================================
  // WHATSAPP STUDENT DATA
  // =========================================

  const getWhatsAppStudent =
    (student) => {
      const stats =
        getStudentDayStats(
          student.studentId
        );

      /*
        IMPORTANT:

        Rebuild pendingItems here from
        subjectsMap so subject/type can
        never be reversed.
      */

      const pendingItems =
        [];

      Object.entries(
        student.subjectsMap ||
        {}
      ).forEach(
        ([
          subject,
          types
        ]) => {
          const cleanSubject =
            String(
              subject
            ).trim();

          if (
            types?.homework &&
            cleanSubject
          ) {
            pendingItems.push({
              subject:
                cleanSubject,

              type:
                "Homework"
            });
          }

          if (
            types?.notebook &&
            cleanSubject
          ) {
            pendingItems.push({
              subject:
                cleanSubject,

              type:
                "Notebook"
            });
          }
        }
      );

      /*
        Fallback if subjectsMap is not
        available.
      */

      if (
        pendingItems.length === 0 &&
        Array.isArray(
          student.pendingItems
        )
      ) {
        student.pendingItems.forEach(
          (item) => {
            const normalized =
              normalizePendingItem(
                item
              );

            if (normalized) {
              pendingItems.push(
                normalized
              );
            }
          }
        );
      }

      return {
        studentId:
          student.studentId,

        studentName:
          student.studentName,

        fatherName:
          student.fatherName,

        phoneNo:
          student.phoneNo,

        phoneNo2:
          student.phoneNo2,

        pendingItems,

        homeworkDays:
          stats.homeworkDays,

        notebookDays:
          stats.notebookDays,

        homeworkCalendarDays:
          stats.homeworkCalendarDays,

        notebookCalendarDays:
          stats.notebookCalendarDays
      };
    };

  // =========================================
  // SELECT STUDENT FOR WHATSAPP
  // =========================================

  const toggleStudent = (
    studentId
  ) => {
    const id =
      String(studentId);

    setSelectedStudents(
      (prev) => {
        const next = {
          ...prev
        };

        if (next[id]) {
          delete next[id];
        } else {
          next[id] = true;
        }

        return next;
      }
    );
  };

  // =========================================
  // SELECT ALL
  // =========================================

  const allSelected =
    studentsForSelectedDate.length >
      0 &&
    studentsForSelectedDate.every(
      (student) =>
        selectedStudents[
          String(
            student.studentId
          )
        ]
    );

  const toggleSelectAll =
    () => {
      if (allSelected) {
        setSelectedStudents(
          {}
        );

        return;
      }

      const next = {};

      studentsForSelectedDate.forEach(
        (student) => {
          next[
            String(
              student.studentId
            )
          ] = true;
        }
      );

      setSelectedStudents(
        next
      );
    };

  // =========================================
  // SELECTED COUNT
  // =========================================

  const selectedCount =
    Object.keys(
      selectedStudents
    ).length;

  // =========================================
  // SEND SINGLE WHATSAPP
  // =========================================

 const sendSingleWhatsApp = async (student) => {
  const phone = getPhone(student);

  if (!phone) {
    setError(`Phone number missing for ${student.studentName}.`);

    Swal.fire({
      icon: "warning",
      title: "Phone Number Missing",
      text: `Phone number is not available for ${student.studentName}.`,
      confirmButtonText: "OK"
    });

    return;
  }

  try {
    setSendingStudentId(String(student.studentId));

    setMessage("");
    setError("");

    const payloadStudent = getWhatsAppStudent(student);

    if (!payloadStudent.pendingItems?.length) {
      setError(
        `No pending Homework/Notebook found for ${student.studentName}.`
      );

      Swal.fire({
        icon: "info",
        title: "No Pending Work",
        text: `No pending Homework or Notebook found for ${student.studentName}.`,
        confirmButtonText: "OK"
      });

      return;
    }

    const res = await axios.post(
  `${API}/api/homeworkNotCompleted/send-whatsapp`,
  {
    teacherId,
    sessionId,
    students: [payloadStudent],
    date: selectedDate,
    className: classSection.className,
    sectionName: classSection.sectionName
  }
);

    const successMessage =
      res.data?.message ||
      `WhatsApp message sent successfully to ${student.studentName}.`;

    setMessage(successMessage);

    // SUCCESS SWEET ALERT
    Swal.fire({
      icon: "success",
      title: "Message Sent Successfully",
      text: `WhatsApp message has been sent to ${student.studentName}.`,
      confirmButtonText: "OK",
      confirmButtonColor: "#16a34a"
    });

  } catch (err) {
    console.error("WhatsApp error:", err);

    const errorMessage =
      err.response?.data?.message ||
      "WhatsApp sending failed.";

    setError(errorMessage);

    // ERROR SWEET ALERT
    Swal.fire({
      icon: "error",
      title: "Message Not Sent",
      text: errorMessage,
      confirmButtonText: "OK"
    });

  } finally {
    setSendingStudentId(null);
  }
};

  // =========================================
  // BULK WHATSAPP
  // =========================================

  const sendBulkWhatsApp = async () => {
  const studentsToSend = studentsForSelectedDate.filter(
    (student) =>
      selectedStudents[String(student.studentId)]
  );

  if (!studentsToSend.length) {
    setError("Please select at least one student.");

    Swal.fire({
      icon: "warning",
      title: "No Student Selected",
      text: "Please select at least one student to send WhatsApp message.",
      confirmButtonText: "OK"
    });

    return;
  }

  const withoutPhone = studentsToSend.filter(
    (student) => !getPhone(student)
  );

  if (withoutPhone.length) {
    setError(
      `Phone number missing for ${withoutPhone.length} selected student(s).`
    );

    Swal.fire({
      icon: "warning",
      title: "Phone Number Missing",
      text: `Phone number is missing for ${withoutPhone.length} selected student(s).`,
      confirmButtonText: "OK"
    });

    return;
  }

  try {
    setBulkSending(true);

    setMessage("");
    setError("");

    const payloadStudents = studentsToSend
      .map((student) => getWhatsAppStudent(student))
      .filter(
        (student) =>
          student.pendingItems?.length
      );

    if (!payloadStudents.length) {
      setError(
        "No pending Homework/Notebook found for selected students."
      );

      Swal.fire({
        icon: "info",
        title: "No Pending Work",
        text: "No pending Homework or Notebook found for selected students.",
        confirmButtonText: "OK"
      });

      return;
    }

    const res = await axios.post(
  `${API}/api/homeworkNotCompleted/send-whatsapp`,
  {
    teacherId,
    sessionId,
    students: payloadStudents,
    date: selectedDate,
    className: classSection.className,
    sectionName: classSection.sectionName
  }
);

    const successMessage =
      res.data?.message ||
      "WhatsApp messages sent successfully.";

    setMessage(successMessage);

    setSelectedStudents({});

    // SUCCESS SWEET ALERT
    Swal.fire({
      icon: "success",
      title: "Messages Sent Successfully",
      text: `WhatsApp messages have been sent to ${payloadStudents.length} student(s).`,
      confirmButtonText: "OK",
      confirmButtonColor: "#16a34a"
    });

  } catch (err) {
    console.error("Bulk WhatsApp error:", err);

    const errorMessage =
      err.response?.data?.message ||
      "WhatsApp sending failed.";

    setError(errorMessage);

    // ERROR SWEET ALERT
    Swal.fire({
      icon: "error",
      title: "Messages Not Sent",
      text: errorMessage,
      confirmButtonText: "OK"
    });

  } finally {
    setBulkSending(false);
  }
};

  // =========================================
  // DATE CHANGE
  // =========================================

  const changeDate = (
    days
  ) => {
    const current =
      new Date(
        `${selectedDate}T00:00:00`
      );

    current.setDate(
      current.getDate() +
        days
    );

    const month =
      String(
        current.getMonth() + 1
      ).padStart(2, "0");

    const day =
      String(
        current.getDate()
      ).padStart(2, "0");

    setSelectedDate(
      `${current.getFullYear()}-${month}-${day}`
    );

    setSelectedStudents(
      {}
    );
  };

  // =========================================
  // STUDENT DROPDOWN
  // =========================================

  const handleStudentChange =
    (e) => {
      setSelectedStudentId(
        e.target.value
      );

      setSelectedStudents(
        {}
      );

      setMessage("");
      setError("");
    };

  // =========================================
  // PRINT HISTORY
  // =========================================

  const printStudentHistory = () => {
    if (!selectedHistoryStudent) return;

    const rows = filteredHistory || [];
    const selectedMonthLabel =
      historyMonth === "all"
        ? "All Months"
        : historyMonths.find(
            (month) => month.value === historyMonth
          )?.label || "All Months";

    const homeworkCount = rows.filter(
      (row) => row.homework
    ).length;

    const notebookCount = rows.filter(
      (row) => row.notebook
    ).length;

    const tableRows = rows.length
      ? rows
          .map(
            (row) => `
              <tr>
                <td>${formatDate(row.date)}</td>
                <td>${String(row.subject || "-")}</td>
                <td class="center">${
                  row.homework
                    ? '<span class="status homework">Not Completed</span>'
                    : "-"
                }</td>
                <td class="center">${
                  row.notebook
                    ? '<span class="status notebook">Not Completed</span>'
                    : "-"
                }</td>
              </tr>
            `
          )
          .join("")
      : `
          <tr>
            <td colspan="4" class="empty">No history found for the selected month.</td>
          </tr>
        `;

    const printWindow = window.open(
      "",
      "_blank",
      "width=1000,height=800"
    );

    if (!printWindow) {
      alert("Please allow pop-ups to print the history.");
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Homework & Notebook History - ${
            selectedHistoryStudent.studentName || "Student"
          }</title>
          <meta charset="UTF-8" />
          <style>
            @page {
              size: A4 portrait;
              margin: 12mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              font-family: Arial, Helvetica, sans-serif;
              color: #111827;
              background: #fff;
              font-size: 12px;
            }

            .school {
              text-align: center;
              font-size: 22px;
              font-weight: 800;
              margin-bottom: 4px;
              color: #111827;
            }

            .school-subtitle {
              text-align: center;
              font-size: 12px;
              color: #6b7280;
              margin-bottom: 12px;
            }

            .line {
              height: 2px;
              background: #1d4ed8;
              margin-bottom: 14px;
            }

            h1 {
              text-align: center;
              font-size: 18px;
              margin: 0 0 14px;
            }

            .student-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              border: 1px solid #d1d5db;
              border-radius: 6px;
              margin-bottom: 12px;
            }

            .student-item {
              padding: 7px 9px;
              border-bottom: 1px solid #e5e7eb;
            }

            .student-item:nth-child(odd) {
              border-right: 1px solid #e5e7eb;
            }

            .student-item:nth-last-child(-n+2) {
              border-bottom: 0;
            }

            .label {
              color: #6b7280;
              font-size: 10px;
              text-transform: uppercase;
              font-weight: 700;
            }

            .value {
              font-weight: 700;
              margin-top: 2px;
            }

            .summary {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 8px;
              margin-bottom: 14px;
            }

            .summary-box {
              border: 1px solid #d1d5db;
              border-radius: 6px;
              padding: 8px;
              text-align: center;
            }

            .summary-number {
              font-size: 17px;
              font-weight: 800;
              margin-top: 2px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }

            thead {
              display: table-header-group;
            }

            tr {
              page-break-inside: avoid;
              break-inside: avoid;
            }

            th, td {
              border: 1px solid #374151;
              padding: 7px 8px;
              vertical-align: middle;
            }

            th {
              background: #f3f4f6;
              font-weight: 800;
              text-align: left;
            }

            th:nth-child(1), td:nth-child(1) {
              width: 18%;
            }

            th:nth-child(2), td:nth-child(2) {
              width: 32%;
            }

            th:nth-child(3), td:nth-child(3),
            th:nth-child(4), td:nth-child(4) {
              width: 25%;
            }

            .center {
              text-align: center;
            }

            .status {
              display: inline-block;
              padding: 4px 7px;
              border-radius: 4px;
              font-size: 10px;
              font-weight: 800;
            }

            .homework {
              background: #fee2e2;
              color: #b91c1c;
            }

            .notebook {
              background: #ffedd5;
              color: #c2410c;
            }

            .empty {
              text-align: center;
              padding: 22px;
              color: #6b7280;
            }

            .footer {
              margin-top: 14px;
              text-align: center;
              color: #6b7280;
              font-size: 10px;
            }

            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="school">Cosmonaut International School (CIS)</div>
          <div class="school-subtitle">Student Academic Record</div>
          <div class="line"></div>

          <h1>Homework &amp; Notebook History</h1>

          <div class="student-grid">
            <div class="student-item">
              <div class="label">Student Name</div>
              <div class="value">${selectedHistoryStudent.studentName || "-"}</div>
            </div>
            <div class="student-item">
              <div class="label">Roll No</div>
              <div class="value">${selectedHistoryStudent.rollNo || "-"}</div>
            </div>
            <div class="student-item">
              <div class="label">Father Name</div>
              <div class="value">${selectedHistoryStudent.fatherName || "-"}</div>
            </div>
            <div class="student-item">
              <div class="label">Class / Section</div>
              <div class="value">${classSection.className || "-"} - ${classSection.sectionName || "-"}</div>
            </div>
          </div>


          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Subject</th>
                <th class="center">Homework</th>
                <th class="center">Notebook</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>

          <div class="footer">Generated from Cosmonaut International School (CIS) • Homework &amp; Notebook Tracking</div>
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => printWindow.close(), 500);
    };
  };

  // =========================================
  // LOADING
  // =========================================

  if (loading) {
    return (
      <div className="p-10 text-center">
        Loading...
      </div>
    );
  }

  // =========================================
  // UI
  // =========================================

  return (
    <>
      <style>
        {`
          @media print {
            body {
              background: white !important;
            }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 overflow-x-hidden">

        {/* =====================================
            HEADER
        ===================================== */}

        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

              <div className="flex items-center gap-3">

                <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                  <i className="fas fa-school"></i>
                </div>

                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    My Class
                  </h1>

                  <p className="text-sm text-gray-500">
                    Homework & Notebook
                    Tracking
                  </p>
                </div>

              </div>

              <button
                type="button"
                onClick={() => {
                  loadReports();
                  loadClassStudents();
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 border rounded-lg text-blue-600 font-semibold bg-white hover:bg-blue-50"
              >
                <i className="fas fa-sync-alt"></i>
                Refresh
              </button>

            </div>

          </div>
        </div>

        <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">

          {/* =====================================
              MESSAGES
          ===================================== */}

          {message && (
            <div className="bg-green-100 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {message}
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* =====================================
              CLASS INFO
          ===================================== */}

          <div className="bg-white border rounded-xl p-5">

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-xs text-blue-600 font-semibold uppercase">
                  Class
                </div>

                <div className="text-xl font-bold text-gray-900 mt-1">
                  {classSection.className ||
                    "-"}
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-xs text-purple-600 font-semibold uppercase">
                  Section
                </div>

                <div className="text-xl font-bold text-gray-900 mt-1">
                  {classSection.sectionName ||
                    "-"}
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-xs text-green-600 font-semibold uppercase">
                  Total Students
                </div>

                <div className="text-xl font-bold text-gray-900 mt-1">
                  {classStudents.length}
                </div>
              </div>

            </div>

          </div>

          {/* =====================================
              FILTERS
          ===================================== */}

          <div className="bg-white border rounded-xl p-4 sm:p-5">

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* DATE */}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Date
                </label>

                <div className="flex gap-2">

                  <button
                    type="button"
                    onClick={() =>
                      changeDate(-1)
                    }
                    className="px-3 border rounded-lg bg-white hover:bg-gray-50"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>

                  <input
                    type="date"
                    value={
                      selectedDate
                    }
                    onChange={(e) => {
                      setSelectedDate(
                        e.target.value
                      );

                      setSelectedStudents(
                        {}
                      );
                    }}
                    className="w-full border rounded-lg px-3 py-2.5"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      changeDate(1)
                    }
                    className="px-3 border rounded-lg bg-white hover:bg-gray-50"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>

                </div>

                <button
                  type="button"
                  onClick={() => {
                    setSelectedDate(
                      getToday()
                    );

                    setSelectedStudents(
                      {}
                    );
                  }}
                  className="mt-2 text-sm text-blue-600 font-semibold hover:underline"
                >
                  Today
                </button>
              </div>

              {/* STUDENT */}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Student
                </label>

                <select
                  value={
                    selectedStudentId
                  }
                  onChange={
                    handleStudentChange
                  }
                  disabled={
                    studentsLoading
                  }
                  className="w-full border rounded-lg px-3 py-2.5 bg-white"
                >
                  <option value="all">
                    All Students
                  </option>

                  {classStudents.map(
                    (student) => (
                      <option
                        key={
                          student._id
                        }
                        value={
                          student._id
                        }
                      >
                        {student.rollNo
                          ? `${student.rollNo} - `
                          : ""}
                        {
                          student.firstName
                        }{" "}
                        {student.lastName ||
                          ""}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* SUBJECT */}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Subject
                </label>

                <select
                  value={
                    subjectFilter
                  }
                  onChange={(e) => {
                    setSubjectFilter(
                      e.target.value
                    );

                    setSelectedStudents(
                      {}
                    );
                  }}
                  className="w-full border rounded-lg px-3 py-2.5 bg-white"
                >
                  <option value="all">
                    All Subjects
                  </option>

                  {availableSubjects.map(
                    (subject) => (
                      <option
                        key={subject}
                        value={subject}
                      >
                        {subject}
                      </option>
                    )
                  )}
                </select>
              </div>

            </div>

            <div className="mt-4 text-sm text-gray-500">
              Date:{" "}
              <b className="text-gray-800">
                {formatDate(
                  selectedDate
                )}
              </b>

              {" • "}

              {selectedStudentId ===
              "all"
                ? "All Students"
                : "Student History"}
            </div>

          </div>

          {/* =====================================
              STUDENTS TABLE
          ===================================== */}

          <div className="bg-white border rounded-xl overflow-hidden">

            <div className="p-4 sm:p-5 bg-gray-50 border-b">

              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">

                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Students
                  </h2>

                  <p className="text-sm text-gray-500 mt-1">
                    Students with pending
                    work on{" "}
                    {formatDate(
                      selectedDate
                    )}
                  </p>
                </div>

                <div className="text-sm text-gray-600">
                  Selected:{" "}
                  <b>
                    {selectedCount}
                  </b>{" "}
                  /{" "}
                  {
                    studentsForSelectedDate.length
                  }
                </div>

              </div>

            </div>

            {reportsLoading ? (
              <div className="p-10 text-center text-gray-500">
                Loading reports...
              </div>
            ) : studentsForSelectedDate.length ===
              0 ? (
              <div className="p-10 text-center">

                <div className="w-14 h-14 mx-auto rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xl">
                  <i className="fas fa-check"></i>
                </div>

                <h3 className="font-semibold text-gray-800 mt-4">
                  No pending work found
                </h3>

                <p className="text-sm text-gray-500 mt-1">
                  No Homework or Notebook
                  report is available for
                  this date.
                </p>

              </div>
            ) : (
              <div className="w-full overflow-x-auto">

                <table className="w-full min-w-[1100px] text-sm">

                  <thead className="bg-gray-100">

                    <tr>

                      <th className="p-3 text-center w-12">
                        <input
                          type="checkbox"
                          checked={
                            allSelected
                          }
                          onChange={
                            toggleSelectAll
                          }
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>

                      <th className="p-3 text-center">
                        #
                      </th>

                      <th className="p-3 text-left">
                        Roll No
                      </th>

                      <th className="p-3 text-left">
                        Student Name
                      </th>

                      <th className="p-3 text-left">
                        Father Name
                      </th>

                      <th className="p-3 text-left">
                        Homework
                      </th>

                      <th className="p-3 text-left">
                        Notebook
                      </th>

                      <th className="p-3 text-center">
                        Homework
                        <br />
                        Days
                      </th>

                      <th className="p-3 text-center">
                        Notebook
                        <br />
                        Days
                      </th>

                      <th className="p-3 text-center">
                        WhatsApp
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {studentsForSelectedDate.map(
                      (
                        student,
                        index
                      ) => {

                        const id =
                          String(
                            student.studentId
                          );

                        const selected =
                          !!selectedStudents[
                            id
                          ];

                        const stats =
                          getStudentDayStats(
                            student.studentId
                          );

                        const phone =
                          getPhone(
                            student
                          );

                        const homeworkSubjects =
                          Object.entries(
                            student.subjectsMap ||
                            {}
                          )
                            .filter(
                              ([
                                ,
                                types
                              ]) =>
                                types.homework
                            )
                            .map(
                              ([
                                subject
                              ]) =>
                                subject
                            );

                        const notebookSubjects =
                          Object.entries(
                            student.subjectsMap ||
                            {}
                          )
                            .filter(
                              ([
                                ,
                                types
                              ]) =>
                                types.notebook
                            )
                            .map(
                              ([
                                subject
                              ]) =>
                                subject
                            );

                        return (
                          <tr
                            key={id}
                            className={`border-t ${
                              selected
                                ? "bg-green-50"
                                : "hover:bg-gray-50"
                            }`}
                          >

                            {/* SELECT */}

                            <td className="p-3 text-center">
                              <input
                                type="checkbox"
                                checked={
                                  selected
                                }
                                onChange={() =>
                                  toggleStudent(
                                    id
                                  )
                                }
                                className="w-4 h-4 cursor-pointer"
                              />
                            </td>

                            {/* NUMBER */}

                            <td className="p-3 text-center text-gray-500">
                              {index + 1}
                            </td>

                            {/* ROLL */}

                            <td className="p-3 font-semibold">
                              {student.rollNo ??
                                "-"}
                            </td>

                            {/* STUDENT NAME */}

                            <td className="p-3">

                              <button
                                type="button"
                                onClick={() =>
                                  openStudentHistory(
                                    student
                                  )
                                }
                                className="font-semibold text-blue-600 hover:text-blue-800 hover:underline text-left"
                                title="View full history"
                              >
                                {
                                  student.studentName
                                }
                              </button>

                              <div className="text-[10px] text-gray-400 mt-1">
                                Click for history
                              </div>

                            </td>

                            {/* FATHER */}

                            <td className="p-3 text-gray-600">
                              {student.fatherName ||
                                "-"}
                            </td>

                            {/* HOMEWORK SUBJECTS */}

                            <td className="p-3">

                              {homeworkSubjects.length >
                              0 ? (
                                <div className="flex flex-wrap gap-1.5">

                                  {homeworkSubjects.map(
                                    (
                                      subject
                                    ) => (
                                      <span
                                        key={
                                          subject
                                        }
                                        className="bg-red-100 text-red-700 border border-red-200 px-2.5 py-1 rounded-md text-xs font-semibold"
                                      >
                                        {
                                          subject
                                        }
                                      </span>
                                    )
                                  )}

                                </div>
                              ) : (
                                <span className="text-gray-400">
                                  -
                                </span>
                              )}

                            </td>

                            {/* NOTEBOOK SUBJECTS */}

                            <td className="p-3">

                              {notebookSubjects.length >
                              0 ? (
                                <div className="flex flex-wrap gap-1.5">

                                  {notebookSubjects.map(
                                    (
                                      subject
                                    ) => (
                                      <span
                                        key={
                                          subject
                                        }
                                        className="bg-orange-100 text-orange-700 border border-orange-200 px-2.5 py-1 rounded-md text-xs font-semibold"
                                      >
                                        {
                                          subject
                                        }
                                      </span>
                                    )
                                  )}

                                </div>
                              ) : (
                                <span className="text-gray-400">
                                  -
                                </span>
                              )}

                            </td>

                            {/* HOMEWORK DAYS */}

                            <td className="p-3 text-center">

                              {stats.homeworkDays >
                              0 ? (
                                <div>

                                  <span className="inline-flex items-center justify-center min-w-10 h-8 px-2 rounded-full bg-red-100 text-red-700 font-bold">
                                    {
                                      stats.homeworkDays
                                    }
                                  </span>

                                </div>
                              ) : (
                                <span className="text-gray-400">
                                  -
                                </span>
                              )}

                            </td>

                            {/* NOTEBOOK DAYS */}

                            <td className="p-3 text-center">

                              {stats.notebookDays >
                              0 ? (
                                <div>

                                  <span className="inline-flex items-center justify-center min-w-10 h-8 px-2 rounded-full bg-orange-100 text-orange-700 font-bold">
                                    {
                                      stats.notebookDays
                                    }
                                  </span>

                                </div>
                              ) : (
                                <span className="text-gray-400">
                                  -
                                </span>
                              )}

                            </td>

                            {/* WHATSAPP */}

                            <td className="p-3 text-center">

                              <button
                                type="button"
                                disabled={
                                  !phone ||
                                  sendingStudentId ===
                                    id ||
                                  bulkSending
                                }
                                onClick={() =>
                                  sendSingleWhatsApp(
                                    student
                                  )
                                }
                                className="inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-3 py-2 rounded-lg text-xs font-semibold"
                              >

                                <i className="fab fa-whatsapp"></i>

                                {sendingStudentId ===
                                id
                                  ? "Sending..."
                                  : "WhatsApp"}

                              </button>

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>
            )}

            {/* =================================
                BULK WHATSAPP
            ================================= */}

            {studentsForSelectedDate.length >
              0 && (
              <div className="border-t bg-gray-50 p-4">

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                  <div className="text-sm text-gray-600">
                    <b>
                      {selectedCount}
                    </b>{" "}
                    student(s) selected
                  </div>

                  <button
                    type="button"
                    disabled={
                      bulkSending ||
                      selectedCount ===
                        0
                    }
                    onClick={
                      sendBulkWhatsApp
                    }
                    className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-5 py-3 rounded-lg font-semibold"
                  >

                    <i className="fab fa-whatsapp text-lg"></i>

                    {bulkSending
                      ? "Sending..."
                      : `Send WhatsApp to Selected (${selectedCount})`}

                  </button>

                </div>

              </div>
            )}

          </div>

        </div>
      </div>

      {/* =================================================
          STUDENT HISTORY MODAL
      ================================================= */}

      {selectedHistoryStudent && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5"
          onClick={() => setSelectedHistoryStudent(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-5xl max-h-[94vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}
            <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-violet-700 text-white px-5 sm:px-6 py-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
                    <i className="fas fa-history text-lg"></i>
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold">Student History</h2>
                    <p className="text-blue-100 text-sm mt-1">
                      {selectedHistoryStudent.studentName || "-"} • Roll No: {selectedHistoryStudent.rollNo || "-"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={printStudentHistory}
                    className="inline-flex items-center gap-2 bg-white text-gray-800 hover:bg-gray-100 px-4 py-2.5 rounded-xl font-bold shadow-sm"
                  >
                    <i className="fas fa-print"></i>
                    Print History
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedHistoryStudent(null)}
                    className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 hover:bg-white/20 flex items-center justify-center"
                    aria-label="Close history"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* FILTER + STUDENT INFO */}
            <div className="p-4 sm:p-5 border-b bg-slate-50">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="text-[11px] uppercase tracking-wide font-bold text-slate-400">Student</div>
                  <div className="font-bold text-slate-800 mt-1 truncate">{selectedHistoryStudent.studentName || "-"}</div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="text-[11px] uppercase tracking-wide font-bold text-slate-400">Father</div>
                  <div className="font-bold text-slate-800 mt-1 truncate">{selectedHistoryStudent.fatherName || "-"}</div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="text-[11px] uppercase tracking-wide font-bold text-slate-400">Class / Section</div>
                  <div className="font-bold text-slate-800 mt-1">
                    {classSection.className || "-"} - {classSection.sectionName || "-"}
                  </div>
                </div>

                <div className="bg-white border border-blue-200 rounded-xl p-3">
                  <label className="text-[11px] uppercase tracking-wide font-bold text-blue-500 block">History Month</label>
                  <select
                    value={historyMonth}
                    onChange={(e) => setHistoryMonth(e.target.value)}
                    className="w-full mt-1 bg-transparent font-bold text-slate-800 outline-none cursor-pointer"
                  >
                    <option value="all">All Months</option>
                    {historyMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

            </div>

            {/* HISTORY TABLE */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-5">
              {filteredHistory.length === 0 ? (
                <div className="min-h-[280px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center text-2xl">
                      <i className="fas fa-calendar-times"></i>
                    </div>
                    <h3 className="font-bold text-slate-800 mt-4">No History Found</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      There is no pending Homework or Notebook record for this month.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-sm border-collapse">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="p-3 text-left font-bold text-slate-700">Date</th>
                          <th className="p-3 text-left font-bold text-slate-700">Subject</th>
                          <th className="p-3 text-center font-bold text-slate-700">Homework</th>
                          <th className="p-3 text-center font-bold text-slate-700">Notebook</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHistory.map((row, index) => (
                          <tr
                            key={`${row.date}-${row.subject}-${index}`}
                            className="border-t border-slate-100 hover:bg-slate-50"
                          >
                            <td className="p-3 font-semibold text-slate-700">
                              <div className="inline-flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500">
                                  <i className="fas fa-calendar-day text-xs"></i>
                                </span>
                                {formatDate(row.date)}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="inline-flex items-center gap-2 font-bold text-slate-800">
                                <span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                                  <i className="fas fa-book text-xs"></i>
                                </span>
                                {row.subject || "-"}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              {row.homework ? (
                                <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 px-3 py-1.5 rounded-lg text-xs font-bold">
                                  <i className="fas fa-times-circle"></i>
                                  Not Completed
                                </span>
                              ) : (
                                <span className="text-slate-300 font-bold">—</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {row.notebook ? (
                                <span className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-700 border border-orange-200 px-3 py-1.5 rounded-lg text-xs font-bold">
                                  <i className="fas fa-times-circle"></i>
                                  Not Completed
                                </span>
                              ) : (
                                <span className="text-slate-300 font-bold">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="border-t bg-white px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs sm:text-sm text-slate-500">
                Showing <b className="text-slate-800">{filteredHistory.length}</b> record(s)
                {historyMonth !== "all" && (
                  <> for <b className="text-slate-800">{historyMonths.find((m) => m.value === historyMonth)?.label}</b></>
                )}
              </div>

              <button
                type="button"
                onClick={() => setSelectedHistoryStudent(null)}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-slate-200 rounded-xl font-bold text-slate-700 bg-white hover:bg-slate-50"
              >
                <i className="fas fa-times"></i>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyClass;